import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { groupsApi, groupHierarchyApi, API_BASE_URL } from '../services/api';
import { GroupTreeSidebar, buildTree, uploadBlobThroughStorage, type GroupNode, type GroupTreeNode, showToast, askConfirm } from 'shared-ui';

// Phase 1: hierarchical group tree (Van Life > Country > Region > City style nesting).
// Feature-flagged while it's proved out here before porting to mango-chat.
const HIERARCHY_ENABLED = process.env.REACT_APP_GROUP_HIERARCHY === 'true';

interface GroupChannel {
  id: string;
  name: string;
  description?: string;
  type: 'general' | 'announcement' | 'training' | 'social' | 'event';
  icon: string;
  isPrivate: boolean;
  allowedRoles?: string[]; // If private, which custom roles can access
  unreadCount: number;
  lastMessage?: {
    sender: string;
    content: string;
    timestamp: string;
  };
}

interface GroupEvent {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location?: string;
  attendees: string[];
  maxAttendees?: number;
  hasChat: boolean;
  chatChannelId?: string;
}

interface Group {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  coverImageUrl?: string;
  category: string;
  memberCount: number;
  isPublic: boolean;
  createdBy: string;
  createdByName: string;
  lastActivity: string;
  isMember: boolean;
  myRole?: 'admin' | 'moderator' | 'member';
  myCustomRoles?: string[];
  myPhotoPostingMode?: 'manual' | 'prompt' | 'auto';
  channels: GroupChannel[];
  customRoles: string[]; // Available custom roles like "Base", "Flyer"
  upcomingEvents: GroupEvent[];
  type?: 'personal' | 'community';
  minimumAdmins?: number | null;
  membershipFee?: number | null;
  membershipPeriod?: 'weekly' | 'yearly' | null;
}

interface LinkedProduct {
  id: string;
  title: string;
  description?: string | null;
  price?: number | null;
  imageUrls?: string[];
  externalUrl?: string | null;
}

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  senderAvatar?: string;
  content: string;
  attachments?: { type: 'image' | 'file'; url: string; name: string }[];
  category?: string;
  linkedProductId?: string;
  linkedProduct?: LinkedProduct | null;
  createdAt: string;
  reactions?: { emoji: string; count: number; users: string[] }[];
}

type ChannelView = 'all' | 'general' | 'product' | 'event' | 'recommendation';
const CHANNEL_VIEWS: { id: ChannelView; label: string; icon: string }[] = [
  { id: 'all', label: 'All', icon: '💬' },
  { id: 'general', label: 'General', icon: '💬' },
  { id: 'product', label: 'Productos', icon: '🛒' },
  { id: 'event', label: 'Eventos', icon: '📅' },
  { id: 'recommendation', label: 'Recomendaciones', icon: '👍' },
];

const GROUP_CATEGORIES = [
  'All',
  'Family',
  'Sports & Fitness',
  'Arts & Creative',
  'Business Networking',
  'Hobbies',
  'Local Community',
  'Education',
  'Gaming',
  'Music',
  'Technology',
];

const CHANNEL_ICONS: Record<string, string> = {
  general: '💬',
  announcement: '📢',
  training: '💪',
  social: '🎉',
  event: '📅',
};

/** Product collage card: photo grid with sticker-style overlays (title, price, highlights, link) */
function ProductCollageCard({ product, groupId }: { product: LinkedProduct; groupId: string }) {
  const images = product.imageUrls || [];
  const highlights = (product.description || '')
    .split(/\n/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 5);
  const productLink = `/groups/${groupId}/product/${product.id}`;

  const renderCollage = () => {
    if (images.length === 0) {
      return (
        <div style={{ aspectRatio: '9/16', maxHeight: '320px', background: 'rgba(0,0,0,0.3)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
          No photos
        </div>
      );
    }
    if (images.length === 1) {
      return (
        <img src={images[0]} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '12px' }} />
      );
    }
    if (images.length === 2) {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', borderRadius: '12px', overflow: 'hidden' }}>
          {images.map((url, i) => (
            <img key={i} src={url} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover' }} />
          ))}
        </div>
      );
    }
    if (images.length === 3) {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '4px', borderRadius: '12px', overflow: 'hidden' }}>
          <img src={images[0]} alt="" style={{ gridColumn: 'span 2', width: '100%', height: '140px', objectFit: 'cover' }} />
          {images.slice(1).map((url, i) => (
            <img key={i} src={url} alt="" style={{ width: '100%', height: '100px', objectFit: 'cover' }} />
          ))}
        </div>
      );
    }
    if (images.length === 4) {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', borderRadius: '12px', overflow: 'hidden' }}>
          {images.map((url, i) => (
            <img key={i} src={url} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover' }} />
          ))}
        </div>
      );
    }
    // 5 images: 2 top, 2 middle, 1 bottom
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', borderRadius: '12px', overflow: 'hidden' }}>
        {images.slice(0, 2).map((url, i) => (
          <img key={i} src={url} alt="" style={{ width: '100%', height: '90px', objectFit: 'cover' }} />
        ))}
        {images.slice(2, 4).map((url, i) => (
          <img key={i} src={url} alt="" style={{ width: '100%', height: '90px', objectFit: 'cover' }} />
        ))}
        <img src={images[4]} alt="" style={{ gridColumn: 'span 2', width: '100%', height: '90px', objectFit: 'cover' }} />
      </div>
    );
  };

  return (
    <Link to={productLink} style={{ display: 'block', maxWidth: '280px', cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}>
      <div style={{ position: 'relative' }}>
        {renderCollage()}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', display: 'flex', flexDirection: 'column', padding: '12px', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <span style={{ padding: '4px 12px', background: 'rgba(50,50,50,0.9)', color: 'white', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>ON SALE</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.95)', borderRadius: '10px', color: '#1a1a1a', fontWeight: '700', fontSize: '13px' }}>
              {product.title}{product.price != null ? ` $${product.price.toLocaleString()}` : ''}
            </div>
            {highlights.map((h, i) => (
              <div key={i} style={{ padding: '4px 10px', background: 'rgba(255,255,255,0.95)', borderRadius: '10px', color: '#1a1a1a', fontSize: '11px' }}>
                ✅ {h}
              </div>
            ))}
          </div>
        </div>
      </div>
      <span style={{ display: 'block', marginTop: '10px', fontSize: '12px', color: '#7289da', fontWeight: '600' }}>
        View product →
      </span>
    </Link>
  );
}

type AiFeatureBillingMode = 'requester' | 'split_immediate' | 'group' | 'split_daily';

interface GroupSettings {
  aiOrganizingAvailable: boolean;
  aiOrganizingEnabled: boolean;
  aiCreditsBalance: number;
  cronSecretSet: boolean;
  creditsPerRun: number;
  aiChatEnabled: boolean;
  aiChatBillingMode: AiFeatureBillingMode;
  translationBillingMode: AiFeatureBillingMode;
  transcriptionBillingMode: AiFeatureBillingMode;
  creditsPerAiReply: number;
  creditsPerTranslation: number;
  creditsPerTranscription: number;
  autoBookingEnabled: boolean;
  autoBookingBillingMode: AiFeatureBillingMode;
  creditsPerAutoBookingDetection: number;
}

interface GroupSettingsDraft {
  aiOrganizingEnabled?: boolean;
  cronSecret?: string;
  addCredits?: string;
  aiChatEnabled?: boolean;
  aiChatBillingMode?: AiFeatureBillingMode;
  translationBillingMode?: AiFeatureBillingMode;
  transcriptionBillingMode?: AiFeatureBillingMode;
  autoBookingEnabled?: boolean;
  autoBookingBillingMode?: AiFeatureBillingMode;
}

interface GroupSettingsModalProps {
  group: Group;
  onClose: () => void;
  onOpen: () => Promise<void>;
  settings: GroupSettings | null | 'error';
  draft: GroupSettingsDraft;
  onDraftChange: (d: GroupSettingsDraft) => void;
  onSave: () => Promise<{ success?: boolean; error?: string }>;
  checkoutSuccessCredits?: string | null;
}

const BILLING_MODE_LABELS: Record<AiFeatureBillingMode, string> = {
  requester: 'Whoever asks pays',
  split_immediate: 'Split across the group, right away',
  group: 'Group wallet pays',
  split_daily: 'Split across the group, once a day',
};

interface AiCreditsTier { id: string; credits: number; priceAUD: number; label: string; runs: number }

function GroupSettingsModal({ group, onClose, onOpen, settings, draft, onDraftChange, onSave, checkoutSuccessCredits }: GroupSettingsModalProps) {
  const [saving, setSaving] = useState(false);
  const [wallet, setWallet] = useState<{ balance: number; currency: string; transactions: Array<{ id: string; type: string; amount: number; description?: string; createdAt: string }> } | null>(null);
  const [polls, setPolls] = useState<Array<{ id: string; question: string; options: string[]; type: string; expiresAt?: string; optionCounts?: number[]; totalVotes?: number; myVote?: number | null; isExpired?: boolean }>>([]);
  const [showCreatePoll, setShowCreatePoll] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLog, setAuditLog] = useState<Array<{ id: string; action: string; actorId: string; details?: unknown; createdAt: string }>>([]);
  const [newPoll, setNewPoll] = useState({ question: '', options: ['', ''] });
  const [aiCreditsTiers, setAiCreditsTiers] = useState<AiCreditsTier[]>([]);
  const [topUpPending, setTopUpPending] = useState<string | null>(null);
  const isAdmin = group.myRole === 'admin' || group.myRole === 'moderator';

  useEffect(() => { onOpen(); }, [group?.id]);

  useEffect(() => {
    if (group?.type === 'community' && group?.id) {
      groupsApi.getGroupAuditLog(group.id).then((res: any) => {
        if (res?.success && Array.isArray(res?.data)) setAuditLog(res.data);
        else setAuditLog([]);
      }).catch(() => setAuditLog([]));
    } else {
      setAuditLog([]);
    }
  }, [group?.id, group?.type]);

  useEffect(() => {
    groupsApi.getAiCreditsTiers().then((res: any) => {
      if (res?.success && Array.isArray(res?.data)) setAiCreditsTiers(res.data);
    }).catch(() => setAiCreditsTiers([]));
  }, []);

  useEffect(() => {
    if (group?.type === 'community') {
      groupsApi.getGroupWallet(group.id).then((res: any) => {
        if (res?.success && res?.data) setWallet(res.data);
      }).catch(() => setWallet(null));
    } else {
      setWallet(null);
    }
  }, [group?.id, group?.type]);

  useEffect(() => {
    if (group?.id) {
      groupsApi.getPolls(group.id).then((res: any) => {
        if (res?.success && Array.isArray(res?.data)) setPolls(res.data);
      }).catch(() => setPolls([]));
    }
  }, [group?.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await onSave();
      if (res?.success) setSaving(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePoll = async () => {
    if (!newPoll.question.trim() || newPoll.options.filter(o => o.trim()).length < 2) {
      showToast('Enter a question and at least 2 options', 'error');
      return;
    }
    const opts = newPoll.options.filter(o => o.trim());
    const res = await groupsApi.createPoll(group.id, { question: newPoll.question.trim(), options: opts });
    if (res?.success) {
      setPolls(prev => [res.data, ...prev]);
      setNewPoll({ question: '', options: ['', ''] });
      setShowCreatePoll(false);
    } else {
      showToast(res?.error || 'Failed to create poll', 'error');
    }
  };

  const handleVote = async (pollId: string, optionIndex: number) => {
    const res = await groupsApi.votePoll(group.id, pollId, optionIndex);
    if (res?.success) {
      setPolls(prev => prev.map(p => {
        if (p.id !== pollId) return p;
        const counts = [...(p.optionCounts || p.options.map(() => 0))];
        const oldIdx = p.myVote;
        if (oldIdx != null && oldIdx >= 0 && oldIdx < counts.length) counts[oldIdx] = Math.max(0, counts[oldIdx] - 1);
        if (optionIndex >= 0 && optionIndex < counts.length) counts[optionIndex] = (counts[optionIndex] || 0) + 1;
        const total = counts.reduce((a, b) => a + b, 0);
        return { ...p, optionCounts: counts, myVote: optionIndex, totalVotes: total };
      }));
    }
  };

  const handleTopUpCredits = async (tierId: string) => {
    setTopUpPending(tierId);
    try {
      const res = await groupsApi.createAiCreditsCheckout(group.id, { tierId });
      if (res?.success && res?.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      if (res?.error) showToast(res.error, 'error');
    } catch {
      showToast('Failed to create payment', 'error');
    } finally {
      setTopUpPending(null);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '420px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 8px 0' }}>Group Settings</h3>
        <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#666' }}>{group.name} • {group.memberCount} members</p>

        {checkoutSuccessCredits && (
          <div style={{ marginBottom: '16px', padding: '12px', background: '#e8f5e9', borderRadius: '8px', color: '#2e7d32', fontSize: '13px' }}>
            Payment successful. {checkoutSuccessCredits} credits have been added. They may take a few seconds to appear.
          </div>
        )}

        {group.type === 'community' && (
          <div style={{ marginBottom: '20px', padding: '12px', background: '#e8f5e9', borderRadius: '8px' }}>
            <strong style={{ display: 'block', marginBottom: '6px' }}>💰 Group Wallet</strong>
            {wallet ? (
              <>
                <p style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>{wallet.currency} ${wallet.balance.toFixed(2)}</p>
                {wallet.transactions?.length > 0 && (
                  <div style={{ marginTop: '10px', fontSize: '12px', maxHeight: '120px', overflowY: 'auto' }}>
                    {wallet.transactions.slice(0, 10).map((t: any) => (
                      <div key={t.id} style={{ padding: '4px 0', borderBottom: '1px solid #ddd' }}>
                        {t.label || t.description || t.type} • +${t.amount?.toFixed(2) ?? '0'}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Loading…</p>
            )}
          </div>
        )}

        {!settings ? (
          <p style={{ color: '#888', fontSize: '14px' }}>Loading…</p>
        ) : settings === 'error' ? (
          <p style={{ color: '#c00', fontSize: '14px' }}>Couldn't load settings.</p>
        ) : (
          <>
            <div style={{ marginBottom: '20px', padding: '12px', background: '#f0f4ff', borderRadius: '8px' }}>
              <strong style={{ display: 'block', marginBottom: '8px' }}>🤖 AI chat & translation</strong>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 600, marginBottom: '8px' }}>
                <input type="checkbox" checked={draft.aiChatEnabled ?? settings.aiChatEnabled} onChange={(e) => onDraftChange({ ...draft, aiChatEnabled: e.target.checked })} />
                Let members @ai-mention an AI reply in chat
              </label>
              <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#666' }}>
                {settings.creditsPerAiReply} credits per reply, {settings.creditsPerTranslation} credits per message translated, {settings.creditsPerTranscription} credits per voice message transcribed.
              </p>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Who pays for AI replies?</label>
              <select
                value={draft.aiChatBillingMode ?? settings.aiChatBillingMode}
                onChange={(e) => onDraftChange({ ...draft, aiChatBillingMode: e.target.value as AiFeatureBillingMode })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', marginBottom: '10px' }}
              >
                {(Object.keys(BILLING_MODE_LABELS) as AiFeatureBillingMode[]).map((mode) => (
                  <option key={mode} value={mode}>{BILLING_MODE_LABELS[mode]}</option>
                ))}
              </select>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Who pays for translations?</label>
              <select
                value={draft.translationBillingMode ?? settings.translationBillingMode}
                onChange={(e) => onDraftChange({ ...draft, translationBillingMode: e.target.value as AiFeatureBillingMode })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', marginBottom: '10px' }}
              >
                {(Object.keys(BILLING_MODE_LABELS) as AiFeatureBillingMode[]).map((mode) => (
                  <option key={mode} value={mode}>{BILLING_MODE_LABELS[mode]}</option>
                ))}
              </select>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Who pays for voice transcriptions?</label>
              <select
                value={draft.transcriptionBillingMode ?? settings.transcriptionBillingMode}
                onChange={(e) => onDraftChange({ ...draft, transcriptionBillingMode: e.target.value as AiFeatureBillingMode })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
              >
                {(Object.keys(BILLING_MODE_LABELS) as AiFeatureBillingMode[]).map((mode) => (
                  <option key={mode} value={mode}>{BILLING_MODE_LABELS[mode]}</option>
                ))}
              </select>
              {(draft.aiChatBillingMode === 'group' || draft.translationBillingMode === 'group' || draft.transcriptionBillingMode === 'group') && group.type !== 'community' && (
                <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#c00' }}>
                  "Group wallet pays" needs a paid/community group's shared wallet - won't save for this group.
                </p>
              )}
            </div>

            <div style={{ marginBottom: '20px', padding: '12px', background: '#fff4e5', borderRadius: '8px' }}>
              <strong style={{ display: 'block', marginBottom: '8px' }}>📅 AI auto-booking</strong>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 600, marginBottom: '8px' }}>
                <input type="checkbox" checked={draft.autoBookingEnabled ?? settings.autoBookingEnabled} onChange={(e) => onDraftChange({ ...draft, autoBookingEnabled: e.target.checked })} />
                Let AI notice when you agree on a restaurant + time and offer to book it
              </label>
              <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#666' }}>
                Only books real, already-listed restaurants - AI always asks who's coming before booking.
                {' '}{settings.creditsPerAutoBookingDetection} credits per detection.
              </p>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Who pays for auto-booking?</label>
              <select
                value={draft.autoBookingBillingMode ?? settings.autoBookingBillingMode}
                onChange={(e) => onDraftChange({ ...draft, autoBookingBillingMode: e.target.value as AiFeatureBillingMode })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
              >
                {(Object.keys(BILLING_MODE_LABELS) as AiFeatureBillingMode[]).map((mode) => (
                  <option key={mode} value={mode}>{BILLING_MODE_LABELS[mode]}</option>
                ))}
              </select>
              {draft.autoBookingBillingMode === 'group' && group.type !== 'community' && (
                <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#c00' }}>
                  "Group wallet pays" needs a paid/community group's shared wallet - won't save for this group.
                </p>
              )}
            </div>

            {!settings.aiOrganizingAvailable ? (
              <div style={{ marginBottom: '20px', padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                <strong style={{ display: 'block', marginBottom: '6px' }}>AI organising</strong>
                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Only available for paid/community groups.</p>
              </div>
            ) : (
            <div style={{ marginBottom: '20px', padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 600, marginBottom: '8px' }}>
                <input type="checkbox" checked={draft.aiOrganizingEnabled ?? settings.aiOrganizingEnabled} onChange={(e) => onDraftChange({ ...draft, aiOrganizingEnabled: e.target.checked })} />
                AI organising
              </label>
              <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>6000 credits per daily run. Need ≥{settings.creditsPerRun} to enable.</p>
              <p style={{ margin: '8px 0 0 0', fontSize: '13px', fontWeight: 600 }}>Credits: {settings.aiCreditsBalance}</p>
              {isAdmin && aiCreditsTiers.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#666' }}>Top up (paid):</p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {aiCreditsTiers.map(t => (
                      <button key={t.id} type="button" onClick={() => handleTopUpCredits(t.id)} disabled={!!topUpPending}
                        style={{ padding: '8px 12px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: topUpPending ? 'wait' : 'pointer', fontSize: '12px' }}>
                        {t.label} ${t.priceAUD.toFixed(2)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                <input type="number" min="0" placeholder="Add credits (manual)" value={draft.addCredits ?? ''} onChange={(e) => onDraftChange({ ...draft, addCredits: e.target.value })} style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }} />
              </div>
            </div>
            )}

            {group.type === 'community' && (
              <div style={{ marginBottom: '20px', padding: '12px', background: '#f9f9f9', borderRadius: '8px' }}>
                <button type="button" onClick={() => setShowAuditLog(!showAuditLog)} style={{ display: 'block', width: '100%', padding: '8px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontWeight: 600, fontSize: '13px' }}>
                  📋 Audit log {showAuditLog ? '▼' : '▶'}
                </button>
                {showAuditLog && (
                  <div style={{ marginTop: '10px', maxHeight: '150px', overflowY: 'auto', fontSize: '12px', color: '#555' }}>
                    {auditLog.length === 0 ? (
                      <p style={{ margin: 0 }}>No entries yet</p>
                    ) : (
                      auditLog.map((log: any) => (
                        <div key={log.id} style={{ padding: '6px 0', borderBottom: '1px solid #eee' }}>
                          <span style={{ fontWeight: 600 }}>{log.action}</span>
                          {log.details && typeof log.details === 'object' && Object.keys(log.details).length > 0 && (
                            <span style={{ marginLeft: '8px', color: '#666' }}>{JSON.stringify(log.details)}</span>
                          )}
                          <span style={{ marginLeft: '8px', fontSize: '11px', color: '#999' }}>{new Date(log.createdAt).toLocaleString()}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginBottom: '20px', padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '13px' }}>Cron secret</label>
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666' }}>Set the secret your cron uses. Never shared.</p>
              <input type="password" placeholder={settings.cronSecretSet ? '•••••••• (change)' : 'Enter new secret'} value={draft.cronSecret ?? ''} onChange={(e) => onDraftChange({ ...draft, cronSecret: e.target.value })} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }} />
            </div>

            <div style={{ marginBottom: '20px', padding: '12px', background: '#f0f4ff', borderRadius: '8px' }}>
              <strong style={{ display: 'block', marginBottom: '8px' }}>📊 Polls</strong>
              {isAdmin && (
                <button type="button" onClick={() => setShowCreatePoll(!showCreatePoll)} style={{ marginBottom: '10px', padding: '6px 12px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                  {showCreatePoll ? 'Cancel' : 'Create poll'}
                </button>
              )}
              {showCreatePoll && (
                <div style={{ marginBottom: '12px', padding: '10px', background: 'white', borderRadius: '6px' }}>
                  <input type="text" placeholder="Question" value={newPoll.question} onChange={(e) => setNewPoll({ ...newPoll, question: e.target.value })} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #ddd', borderRadius: '6px' }} />
                  {newPoll.options.map((o, i) => (
                    <input key={i} type="text" placeholder={`Option ${i + 1}`} value={o} onChange={(e) => {
                      const arr = [...newPoll.options]; arr[i] = e.target.value; setNewPoll({ ...newPoll, options: arr });
                    }} style={{ width: '100%', padding: '6px', marginBottom: '6px', border: '1px solid #ddd', borderRadius: '6px' }} />
                  ))}
                  <button type="button" onClick={() => setNewPoll({ ...newPoll, options: [...newPoll.options, ''] })} style={{ fontSize: '11px', marginRight: '8px' }}>+ Option</button>
                  <button type="button" onClick={handleCreatePoll} style={{ padding: '6px 12px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Create</button>
                </div>
              )}
              {polls.length > 0 && (
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {polls.map(p => {
                    const opts = (p.options as string[] || []);
                    const counts = p.optionCounts || opts.map(() => 0);
                    const total = p.totalVotes ?? counts.reduce((a, b) => a + b, 0);
                    const expired = p.isExpired || (p.expiresAt && new Date(p.expiresAt) < new Date());
                    return (
                      <div key={p.id} style={{ padding: '8px', marginBottom: '8px', background: 'white', borderRadius: '6px', border: `1px solid ${expired ? '#ccc' : '#ddd'}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <span style={{ fontWeight: 600, fontSize: '13px' }}>{p.question}</span>
                          {expired && <span style={{ fontSize: '10px', color: '#888', background: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>Expired</span>}
                        </div>
                        {opts.map((opt, i) => {
                          const cnt = counts[i] ?? 0;
                          const pct = total > 0 ? Math.round((cnt / total) * 100) : 0;
                          const isMyVote = p.myVote === i;
                          return (
                            <button key={i} type="button" onClick={() => !expired && handleVote(p.id, i)} disabled={expired}
                              style={{ display: 'block', width: '100%', padding: '6px', marginBottom: '4px', textAlign: 'left', border: `1px solid ${isMyVote ? '#667eea' : '#eee'}`, borderRadius: '4px', cursor: expired ? 'not-allowed' : 'pointer', fontSize: '12px', background: isMyVote ? 'rgba(102,126,234,0.1)' : 'white' }}>
                              {opt} {total > 0 && `(${cnt}${pct > 0 ? `, ${pct}%` : ''})`}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
              {polls.length === 0 && !showCreatePoll && <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>No polls yet.</p>}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} style={{ padding: '8px 16px', background: '#eee', color: '#333', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
              <button type="button" onClick={handleSave} disabled={saving} style={{ padding: '8px 16px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: saving ? 'wait' : 'pointer' }}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </>
        )}

        <button type="button" onClick={onClose} style={{ marginTop: '16px', padding: '8px 16px', background: 'transparent', color: '#666', border: 'none', cursor: 'pointer', fontSize: '13px' }}>Close</button>
      </div>
    </div>
  );
}

const Groups: React.FC = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'my-groups' | 'discover' | 'create'>('my-groups');
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<GroupChannel | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  // 📷 = take a photo with the camera (phones); 📎 = pick existing photos. Both share handleAttachPhotos.
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [discoverGroups, setDiscoverGroups] = useState<Group[]>([]);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [showManageRoles, setShowManageRoles] = useState(false);
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [channelViewFilter, setChannelViewFilter] = useState<ChannelView>('all');
  const [messageTag, setMessageTag] = useState<'' | 'product' | 'event' | 'recommendation' | 'general'>('');
  const [channelPolls, setChannelPolls] = useState<Array<{ id: string; question: string; options: string[]; optionCounts?: number[]; totalVotes?: number; myVote?: number | null; isExpired?: boolean; expiresAt?: string }>>([]);
  const [manageRolesMembers, setManageRolesMembers] = useState<Array<{ id: string; userId: string; role: string; name: string; email?: string }>>([]);
  const [inviteAdminEmail, setInviteAdminEmail] = useState('');
  const [inviteAdminSending, setInviteAdminSending] = useState(false);
  const [inviteLinkGenerating, setInviteLinkGenerating] = useState(false);
  const [generatedInviteLink, setGeneratedInviteLink] = useState('');
  const [auditLog, setAuditLog] = useState<Array<{ id: string; action: string; actorId: string; details?: unknown; createdAt: string }>>([]);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [groupSettings, setGroupSettings] = useState<GroupSettings | null | 'error'>(null);
  const [groupSettingsDraft, setGroupSettingsDraft] = useState<GroupSettingsDraft>({});
  const [checkoutSuccessCredits, setCheckoutSuccessCredits] = useState<string | null>(null);
  const [checkoutMembershipSuccess, setCheckoutMembershipSuccess] = useState(false);
  const [membershipCheckoutPending, setMembershipCheckoutPending] = useState(false);
  const [joinCheckoutPending, setJoinCheckoutPending] = useState<string | null>(null);
  const [joinConfirmGroup, setJoinConfirmGroup] = useState<Group | null>(null);

  // Phase 1: hierarchical group tree state. selectedNodeId tracks the tree selection; when it
  // matches a direct membership in `groups` the existing selectedGroup panel below is used
  // unchanged. When it's only visible via roll-up/roll-down (not a direct membership), the
  // lightweight aggregated feed panel further down renders instead.
  const [treeNodes, setTreeNodes] = useState<GroupNode[]>([]);
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set());
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [nodeAncestors, setNodeAncestors] = useState<GroupNode[]>([]);
  const [nodeFeed, setNodeFeed] = useState<any[]>([]);
  const [nodeFeedScope, setNodeFeedScope] = useState<'node' | 'subtree'>('subtree');
  const [nodeFeedLoading, setNodeFeedLoading] = useState(false);
  const [newChildName, setNewChildName] = useState('');
  const [creatingChild, setCreatingChild] = useState(false);
  const [membershipInfo, setMembershipInfo] = useState<{ weeksMember: number; isLoyaltyEligible: boolean; platformFeePercent: number; feeDue?: boolean } | null>(null);

  // Create group form
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    category: '',
    isPublic: true,
    customRoles: '',
    type: 'personal' as 'personal' | 'community',
    membershipFee: '',
    membershipPeriod: 'weekly' as 'weekly' | 'yearly',
  });

  // Create channel form
  const [newChannel, setNewChannel] = useState({
    name: '',
    description: '',
    type: 'general' as GroupChannel['type'],
    isPrivate: false,
    allowedRoles: [] as string[],
  });

  // Create product form (group marketplace)
  const [newProduct, setNewProduct] = useState({
    title: '',
    description: '',
    price: '',
    imageUrls: '', // comma-separated URLs
  });

  // Create event form
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    maxAttendees: '',
    createChat: true,
    photoPromptDelayMinutes: 60,
  });

  useEffect(() => {
    loadGroups();
  }, []);

  useEffect(() => {
    const state = routerLocation.state as { selectGroupId?: string; createFamily?: boolean } | undefined;
    if (!state) return;
    if (state.selectGroupId && groups.length > 0) {
      const g = groups.find((gr) => gr.id === state.selectGroupId);
      if (g) {
        setSelectedGroup(g);
        setActiveTab('my-groups');
        const firstChannel = g.channels?.[0];
        if (firstChannel) setSelectedChannel(firstChannel);
      }
      navigate(routerLocation.pathname, { replace: true, state: {} });
    } else if (state.createFamily) {
      setActiveTab('create');
      setNewGroup((prev) => ({ ...prev, category: 'Family' }));
      navigate(routerLocation.pathname, { replace: true, state: {} });
    }
  }, [groups, routerLocation.state, routerLocation.pathname, navigate]);

  // Checkout return: select group and open settings after successful purchase
  useEffect(() => {
    const params = new URLSearchParams(routerLocation.search);
    const checkout = params.get('checkout');
    const groupId = params.get('group');
    const credits = params.get('credits');
    if ((checkout !== 'success' && checkout !== 'membership_success' && checkout !== 'join_success') || !groupId) return;

    if (checkout === 'join_success') {
      loadGroups().then((refreshed) => {
        const g = refreshed?.find((gr: Group) => gr.id === groupId);
        if (g) {
          setSelectedGroup(g);
          setActiveTab('my-groups');
          const firstChannel = g.channels?.[0];
          if (firstChannel) setSelectedChannel(firstChannel);
        }
      });
      navigate(routerLocation.pathname, { replace: true });
      return;
    }

    if (groups.length === 0) return;
    const g = groups.find((gr) => gr.id === groupId);
    if (g) {
      setSelectedGroup(g);
      setActiveTab('my-groups');
      const firstChannel = g.channels?.[0];
      if (firstChannel) setSelectedChannel(firstChannel);
      if (checkout === 'membership_success') {
        setCheckoutMembershipSuccess(true);
        setTimeout(() => setCheckoutMembershipSuccess(false), 5000);
      } else {
        setShowGroupSettings(true);
        setGroupSettings(null);
        if (credits) setCheckoutSuccessCredits(credits);
      }
    }
    navigate(routerLocation.pathname, { replace: true });
  }, [groups, routerLocation.search, routerLocation.pathname, navigate]);

  useEffect(() => {
    if (selectedGroup && selectedChannel) {
      loadChannelMessages(selectedGroup.id, selectedChannel.id, channelViewFilter);
    } else {
      setMessages([]);
    }
  }, [selectedGroup, selectedChannel, channelViewFilter]);

  useEffect(() => {
    if (selectedGroup?.membershipFee && selectedGroup.membershipFee > 0) {
      groupsApi.getMembershipInfo(selectedGroup.id).then((res: any) => {
        if (res?.success && res?.data) setMembershipInfo(res.data);
        else setMembershipInfo(null);
      }).catch(() => setMembershipInfo(null));
    } else {
      setMembershipInfo(null);
    }
  }, [selectedGroup?.id, selectedGroup?.membershipFee]);

  const loadTree = async () => {
    if (!HIERARCHY_ENABLED) return;
    try {
      const res = await groupHierarchyApi.getMyTree();
      if (res?.success) setTreeNodes(res.data || []);
    } catch (err) {
      console.error('Load group tree error:', err);
    }
  };

  useEffect(() => {
    loadTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleNode = (id: string) => {
    setExpandedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectNode = async (id: string) => {
    setSelectedNodeId(id);
    const directMembership = groups.find((g) => g.id === id && g.isMember);
    if (directMembership) {
      // Falls through to the existing full-featured panel below, unchanged.
      setSelectedGroup(directMembership);
      setSelectedChannel(directMembership.channels[0] || null);
      setNodeFeed([]);
      setNodeAncestors([]);
      return;
    }
    // Visible only via roll-up/roll-down aggregation, not a direct membership — show the
    // lightweight read-mostly aggregated panel instead of the full chat UI.
    setSelectedGroup(null);
    setSelectedChannel(null);
    setNodeFeedLoading(true);
    try {
      const [ancestorsRes, feedRes] = await Promise.all([
        groupHierarchyApi.getAncestors(id),
        groupHierarchyApi.getFeed(id, nodeFeedScope),
      ]);
      setNodeAncestors(ancestorsRes?.success ? ancestorsRes.data || [] : []);
      setNodeFeed(feedRes?.success ? feedRes.data || [] : []);
    } catch (err) {
      console.error('Load node feed error:', err);
      setNodeAncestors([]);
      setNodeFeed([]);
    } finally {
      setNodeFeedLoading(false);
    }
  };

  useEffect(() => {
    if (selectedNodeId && !selectedGroup) {
      handleSelectNode(selectedNodeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeFeedScope]);

  const handleCreateChild = async () => {
    const parentId = selectedNodeId || selectedGroup?.id;
    if (!parentId || !newChildName.trim()) return;
    setCreatingChild(true);
    try {
      const res = await groupHierarchyApi.createChild(parentId, { name: newChildName.trim() });
      if (res?.success) {
        setNewChildName('');
        await loadTree();
        setExpandedNodeIds((prev) => new Set(prev).add(parentId));
      } else {
        showToast(res?.error || 'Failed to create sub-group', 'error');
      }
    } catch (err) {
      console.error('Create sub-group error:', err);
      showToast('Failed to create sub-group', 'error');
    } finally {
      setCreatingChild(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadGroups();
      await loadTree();
      if (selectedGroup && selectedChannel) await loadChannelMessages(selectedGroup.id, selectedChannel.id, channelViewFilter);
    } finally {
      setRefreshing(false);
    }
  };

  const loadGroups = async () => {
    try {
      const res = await groupsApi.getMyGroups();
      if (res?.success && Array.isArray(res?.data)) {
        const mapped: Group[] = res.data.map((g: any) => ({
          id: g.id,
          name: g.name ?? '',
          description: g.description ?? '',
          imageUrl: g.imageUrl,
          coverImageUrl: g.coverImageUrl,
          category: g.category ?? 'Other',
          memberCount: g.memberCount ?? g._count?.members ?? 0,
          isPublic: g.isPublic ?? true,
          createdBy: g.createdBy ?? '',
          createdByName: g.createdByName ?? g.name ?? '',
          lastActivity: g.lastActivity ?? g.updatedAt ?? new Date().toISOString(),
          isMember: true,
          myRole: (g.myRole as 'admin' | 'moderator' | 'member') ?? 'member',
          myCustomRoles: Array.isArray(g.myCustomRoles) ? g.myCustomRoles : [],
          channels: (Array.isArray(g.channels) ? g.channels : []).map((c: any) => {
            const t = (c.type ?? 'general').replace('announcements', 'announcement');
            return {
              id: c.id,
              name: c.name ?? '',
              description: c.description,
              type: (['general', 'announcement', 'training', 'social', 'event'].includes(t) ? t : 'general') as GroupChannel['type'],
              icon: CHANNEL_ICONS[t as keyof typeof CHANNEL_ICONS] ?? '💬',
              isPrivate: !!c.isPrivate || (Array.isArray(c.memberIds) && c.memberIds.length > 0),
              allowedRoles: c.allowedRoles ?? (Array.isArray(c.adminIds) ? c.adminIds : undefined),
              unreadCount: c.unreadCount ?? 0,
              lastMessage: c.lastMessage,
            };
          }),
          customRoles: Array.isArray(g.customRoles) ? g.customRoles : [],
          upcomingEvents: Array.isArray(g.upcomingEvents) ? g.upcomingEvents : [],
          type: g.type === 'community' ? 'community' : 'personal',
          minimumAdmins: g.minimumAdmins ?? null,
          membershipFee: g.membershipFee ?? null,
          membershipPeriod: g.membershipPeriod ?? null,
        }));
        setGroups(mapped);
        setLoading(false);
        return mapped;
      }
    } catch (err) {
      console.error('Load groups failed:', err);
      setGroups([]);
    }
    setLoading(false);
    return [];
  };

  const loadChannelMessages = async (groupId: string, channelId: string, categoryFilter?: ChannelView) => {
    try {
      const category = categoryFilter && categoryFilter !== 'all' ? categoryFilter : undefined;
      const res = await groupsApi.getGroupMessages(groupId, 1, channelId, category);
      if (res?.success && Array.isArray(res?.data)) {
        const mapped: Message[] = res.data.map((m: any) => ({
          id: m.id,
          senderId: m.userId ?? m.sender?.id ?? m.senderId,
          senderName: m.sender ? `${m.sender.firstName ?? ''} ${m.sender.lastName ?? ''}`.trim() || m.senderName : (m.senderName ?? 'Unknown'),
          senderAvatar: m.sender?.profilePhotoUrl ?? m.senderAvatar,
          content: m.content ?? '',
          attachments: m.attachments,
          category: m.category,
          linkedProductId: m.linkedProductId,
          linkedProduct: m.linkedProduct,
          createdAt: m.createdAt ? new Date(m.createdAt).toISOString() : new Date().toISOString(),
          reactions: m.reactions,
        }));
        setMessages(mapped);
      } else {
        setMessages([]);
      }
    } catch (err) {
      console.error('Load channel messages failed:', err);
      setMessages([]);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChannel || !selectedGroup || !user) return;

    const content = newMessage.trim();
    setNewMessage('');

    try {
      const res = await groupsApi.sendGroupMessage(selectedGroup.id, content, {
        channelId: selectedChannel.id,
        category: messageTag || undefined,
      });
      if (res?.success) {
        await loadChannelMessages(selectedGroup.id, selectedChannel.id, channelViewFilter);
      } else {
        setNewMessage(content);
        console.error('Send message failed:', res?.error);
      }
    } catch (err) {
      console.error('Send message failed:', err);
      setNewMessage(content);
    }
  };

  const handleAttachPhotos = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || !selectedChannel || !selectedGroup || !user) return;
    setUploadingPhotos(true);
    try {
      const uploaded = await Promise.all(
        Array.from(fileList).map((file) =>
          uploadBlobThroughStorage({
            apiBaseUrl: API_BASE_URL,
            tokenKey: 'mangoPersonalToken',
            file,
            meta: { fileType: 'ATTACHMENT' },
          })
        )
      );
      const attachments = uploaded
        .filter((f) => !!f.url)
        .map((f) => ({ type: 'image' as const, url: f.url as string, name: f.filename }));
      if (attachments.length === 0) {
        showToast('Upload succeeded but no file URL was returned.', 'error');
        return;
      }
      const res = await groupsApi.sendGroupMessage(selectedGroup.id, '', {
        channelId: selectedChannel.id,
        type: 'image',
        attachments,
      });
      if (res?.success) {
        await loadChannelMessages(selectedGroup.id, selectedChannel.id, channelViewFilter);
      } else {
        showToast(res?.error || 'Failed to share photos', 'error');
      }
    } catch (err) {
      console.error('Attach photos failed:', err);
      showToast('Failed to upload photos', 'error');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleChangePhotoPostingMode = async (mode: 'manual' | 'prompt' | 'auto') => {
    if (!selectedGroup) return;
    const previous = selectedGroup.myPhotoPostingMode;
    setSelectedGroup({ ...selectedGroup, myPhotoPostingMode: mode });
    setGroups((prev) => prev.map((g) => (g.id === selectedGroup.id ? { ...g, myPhotoPostingMode: mode } : g)));
    const res = await groupsApi.setPhotoPostingMode(selectedGroup.id, mode);
    if (!res?.success) {
      setSelectedGroup((g) => (g ? { ...g, myPhotoPostingMode: previous } : g));
      setGroups((prev) => prev.map((g) => (g.id === selectedGroup.id ? { ...g, myPhotoPostingMode: previous } : g)));
      showToast(res?.error || 'Failed to update photo posting preference', 'error');
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name || !newGroup.category) {
      showToast('Please fill in group name and category', 'error');
      return;
    }

    try {
      const res = await groupsApi.createGroup({
        name: newGroup.name,
        description: newGroup.description || '',
        category: newGroup.category,
        isPublic: newGroup.isPublic,
        type: newGroup.type,
        membershipFee: newGroup.membershipFee ? parseFloat(newGroup.membershipFee) : undefined,
        membershipPeriod: newGroup.membershipFee ? newGroup.membershipPeriod : undefined,
      });
      if (res?.success && res?.data) {
        setActiveTab('my-groups');
        setNewGroup({ name: '', description: '', category: '', isPublic: true, customRoles: '', type: 'personal', membershipFee: '', membershipPeriod: 'weekly' });
        const list = await loadGroups();
        const created = list.find((g: Group) => g.id === res.data.id);
        if (created) {
          setSelectedGroup(created);
          setSelectedChannel(created.channels[0] || null);
        }
      } else {
        const err = res?.error || 'Failed to create group';
        if (typeof err === 'string' && err.toLowerCase().includes('one free group')) {
          askConfirm({ title: 'You can only admin one free group', message: 'To create another, add a membership fee (min 50¢/week or $25/year) when creating.', confirmLabel: 'OK', infoOnly: true });
        } else {
          showToast(err, 'error');
        }
      }
    } catch (err) {
      console.error('Create group failed:', err);
      showToast('Failed to create group. Please try again.', 'error');
    }
  };

  const handleCreateChannel = async () => {
    if (!newChannel.name || !selectedGroup) return;

    try {
      const res = await groupsApi.createGroupChannel(selectedGroup.id, {
        name: newChannel.name,
        description: newChannel.description || undefined,
        type: newChannel.type,
        isPrivate: newChannel.isPrivate,
        icon: CHANNEL_ICONS[newChannel.type],
        allowedRoles: newChannel.isPrivate ? newChannel.allowedRoles : undefined,
      });
      if (res?.success) {
        setShowCreateChannel(false);
        setNewChannel({ name: '', description: '', type: 'general', isPrivate: false, allowedRoles: [] });
        const updatedList = await loadGroups();
        const updated = updatedList.find((g: Group) => g.id === selectedGroup.id);
        if (updated) setSelectedGroup(updated);
      } else {
        showToast(res?.error || 'Failed to create channel', 'error');
      }
    } catch (err) {
      console.error('Create channel failed:', err);
      showToast('Failed to create channel. Please try again.', 'error');
    }
  };

  const handleCreateProduct = async (shareInChat = false) => {
    if (!newProduct.title.trim() || !selectedGroup || !selectedChannel) return;
    try {
      const imageUrls = newProduct.imageUrls.trim() ? newProduct.imageUrls.split(/[\s,]+/).filter(Boolean).slice(0, 5) : [];
      const res = await groupsApi.createGroupProduct(selectedGroup.id, {
        title: newProduct.title.trim(),
        description: newProduct.description.trim() || undefined,
        price: newProduct.price ? parseFloat(newProduct.price) : undefined,
        imageUrls,
      });
      if (res?.success && res?.data) {
        setShowCreateProduct(false);
        setNewProduct({ title: '', description: '', price: '', imageUrls: '' });
        if (shareInChat) {
          await groupsApi.sendGroupMessage(selectedGroup.id, `🛒 ${newProduct.title.trim()}`, {
            channelId: selectedChannel.id,
            category: 'product',
            linkedProductId: res.data.id,
          });
          await loadChannelMessages(selectedGroup.id, selectedChannel.id, channelViewFilter);
        }
      } else {
        showToast(res?.error || 'Failed to create product', 'error');
      }
    } catch (err) {
      console.error('Create product failed:', err);
      showToast('Failed to create product. Please try again.', 'error');
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.date || !selectedGroup) return;

    try {
      const res = await groupsApi.createGroupEvent(selectedGroup.id, {
        title: newEvent.title,
        description: newEvent.description || undefined,
        date: newEvent.date,
        time: newEvent.time || undefined,
        location: newEvent.location || undefined,
        maxAttendees: newEvent.maxAttendees ? parseInt(newEvent.maxAttendees) : undefined,
        createChat: newEvent.createChat,
        photoPromptDelayMinutes: newEvent.photoPromptDelayMinutes,
      });
      if (res?.success) {
        setShowCreateEvent(false);
        setNewEvent({ title: '', description: '', date: '', time: '', location: '', maxAttendees: '', createChat: true, photoPromptDelayMinutes: 60 });
        await loadGroups();
      } else {
        showToast(res?.error || 'Failed to create event', 'error');
      }
    } catch (err) {
      console.error('Create event failed:', err);
      showToast('Failed to create event. Please try again.', 'error');
    }
  };

  const handleJoinGroup = async (group: Group) => {
    if (group.membershipFee != null && group.membershipFee > 0) {
      setJoinConfirmGroup(group);
      return;
    }
    try {
      const res = await groupsApi.joinGroup(group.id);
      if (res?.success) {
        await loadGroups();
        if (activeTab === 'discover') loadDiscoverGroups({ category: selectedCategory, query: searchQuery });
      } else {
        showToast(res?.error || 'Failed to join group', 'error');
      }
    } catch (err) {
      console.error('Join group failed:', err);
      showToast('Failed to join group. Please try again.', 'error');
    }
  };

  const handleConfirmJoinCheckout = async () => {
    const group = joinConfirmGroup;
    if (!group) return;
    setJoinConfirmGroup(null);
    setJoinCheckoutPending(group.id);
    try {
      const res = await groupsApi.createJoinCheckout(group.id);
      if (res?.success && res?.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      showToast(res?.error || 'Failed to create payment', 'error');
    } catch (err) {
      console.error('Join checkout failed:', err);
      showToast('Failed to create payment. Please try again.', 'error');
    } finally {
      setJoinCheckoutPending(null);
    }
  };

  const handlePayMembership = async () => {
    if (!selectedGroup) return;
    setMembershipCheckoutPending(true);
    try {
      const res = await groupsApi.createMembershipCheckout(selectedGroup.id);
      if (res?.success && res?.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      if (res?.error) showToast(res.error, 'error');
    } catch {
      showToast('Failed to create payment', 'error');
    } finally {
      setMembershipCheckoutPending(false);
    }
  };

  const handleLeaveGroup = async () => {
    if (!selectedGroup) return;
    if (!(await askConfirm({ title: `Leave "${selectedGroup.name}"?`, confirmLabel: 'Leave group', danger: true }))) return;
    try {
      const res = await groupsApi.leaveGroup(selectedGroup.id);
      if (res?.success) {
        setSelectedGroup(null);
        setSelectedChannel(null);
        setMessages([]);
        await loadGroups();
      } else {
        const err = res?.error || 'Failed to leave group';
        if (typeof err === 'string' && err.toLowerCase().includes('requires at least') && err.toLowerCase().includes('admins')) {
          if (await askConfirm({ title: 'Promote another admin first', message: `${err}\n\nOpen Manage Roles to promote another admin?`, confirmLabel: 'Open Manage Roles' })) {
            setShowGroupSettings(false);
            setShowManageRoles(true);
          }
        } else {
          showToast(err, 'error');
        }
      }
    } catch (err) {
      console.error('Leave group failed:', err);
      showToast('Failed to leave group. Please try again.', 'error');
    }
  };

  const myGroups = groups.filter(g => g.isMember);

  const loadDiscoverGroups = React.useCallback(async (opts?: { category?: string; query?: string }) => {
    const cat = opts?.category ?? 'All';
    const q = opts?.query ?? '';
    setDiscoverLoading(true);
    try {
      const res = await groupsApi.discoverGroups({
        category: cat !== 'All' ? cat : undefined,
        query: (q || '').trim() || undefined,
      });
      if (res?.success && Array.isArray(res?.data)) {
        const mapped: Group[] = res.data.map((g: any) => ({
          id: g.id,
          name: g.name ?? '',
          description: g.description ?? '',
          imageUrl: g.imageUrl,
          coverImageUrl: g.coverImageUrl,
          category: g.category ?? 'Other',
          memberCount: g.memberCount ?? g._count?.members ?? 0,
          isPublic: g.isPublic ?? true,
          createdBy: g.createdBy ?? '',
          createdByName: g.createdByName ?? g.name ?? '',
          lastActivity: g.lastActivity ?? g.updatedAt ?? new Date().toISOString(),
          isMember: false,
          myRole: 'member' as const,
          myCustomRoles: [],
          type: g.type === 'community' ? 'community' : 'personal',
          minimumAdmins: g.minimumAdmins ?? null,
          membershipFee: g.membershipFee ?? null,
          membershipPeriod: g.membershipPeriod ?? null,
          channels: (Array.isArray(g.channels) ? g.channels : []).map((c: any) => ({
            id: c.id,
            name: c.name ?? '',
            description: c.description,
            type: (c.type ?? 'general') as GroupChannel['type'],
            icon: CHANNEL_ICONS[c.type] ?? '💬',
            isPrivate: !!c.isPrivate,
            allowedRoles: c.allowedRoles,
            unreadCount: 0,
            lastMessage: undefined,
          })),
          customRoles: [],
          upcomingEvents: [],
        }));
        setDiscoverGroups(mapped);
      } else {
        setDiscoverGroups([]);
      }
    } catch (err) {
      console.error('Load discover groups failed:', err);
      setDiscoverGroups([]);
    } finally {
      setDiscoverLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'discover') loadDiscoverGroups({ category: selectedCategory, query: searchQuery });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadDiscoverGroups stable; search runs on Search click
  }, [activeTab, selectedCategory]);

  useEffect(() => {
    if (showManageRoles && selectedGroup?.id) {
      groupsApi.getGroupMembers(selectedGroup.id).then((res: any) => {
        if (res?.success && Array.isArray(res?.data)) setManageRolesMembers(res.data);
      }).catch(() => setManageRolesMembers([]));
    }
  }, [showManageRoles, selectedGroup?.id]);

  useEffect(() => {
    if (selectedGroup?.id) {
      groupsApi.getPolls(selectedGroup.id).then((res: any) => {
        if (res?.success && Array.isArray(res?.data)) setChannelPolls(res.data);
      }).catch(() => setChannelPolls([]));
    } else {
      setChannelPolls([]);
    }
  }, [selectedGroup?.id]);

  const handleChannelVote = async (pollId: string, optionIndex: number) => {
    if (!selectedGroup) return;
    const res = await groupsApi.votePoll(selectedGroup.id, pollId, optionIndex);
    if (res?.success) {
      setChannelPolls(prev => prev.map(p => {
        if (p.id !== pollId) return p;
        const counts = [...(p.optionCounts || p.options.map(() => 0))];
        const oldIdx = p.myVote;
        if (oldIdx != null && oldIdx >= 0 && oldIdx < counts.length) counts[oldIdx] = Math.max(0, counts[oldIdx] - 1);
        if (optionIndex >= 0 && optionIndex < counts.length) counts[optionIndex] = (counts[optionIndex] || 0) + 1;
        return { ...p, optionCounts: counts, myVote: optionIndex, totalVotes: counts.reduce((a, b) => a + b, 0) };
      }));
    }
  };

  const filteredDiscoverGroups = discoverGroups;

  const canAccessChannel = (channel: GroupChannel) => {
    if (!channel.isPrivate) return true;
    if (selectedGroup?.myRole === 'admin' || selectedGroup?.myRole === 'moderator') return true;
    if (!channel.allowedRoles || channel.allowedRoles.length === 0) return true;
    return channel.allowedRoles.some(role => selectedGroup?.myCustomRoles?.includes(role));
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 160px)', gap: '0' }}>
      {/* Groups Sidebar */}
      <div style={{
        width: '280px',
        background: '#2f3136',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: '16px 0 0 16px'
      }}>
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', gap: '8px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', color: 'white' }}>👥 Groups</h2>
            <button type="button" onClick={handleRefresh} disabled={refreshing} style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '6px', color: 'white', cursor: refreshing ? 'not-allowed' : 'pointer', fontSize: '12px' }}>
              {refreshing ? '…' : 'Refresh'}
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '5px' }}>
            {[
              { id: 'my-groups', label: 'My Groups' },
              { id: 'discover', label: 'Discover' },
              { id: 'create', label: '+' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id as any);
                  setSelectedGroup(null);
                  setSelectedChannel(null);
                }}
                style={{
                  flex: tab.id === 'create' ? 0 : 1,
                  padding: tab.id === 'create' ? '8px 12px' : '8px',
                  background: activeTab === tab.id ? '#5865f2' : 'rgba(255,255,255,0.1)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: activeTab === tab.id ? '600' : '400'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Group List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
          {activeTab === 'my-groups' && HIERARCHY_ENABLED && (
            treeNodes.length === 0 ? (
              <div style={{ padding: '30px 15px', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                <span style={{ fontSize: '32px' }}>👥</span>
                <p style={{ marginTop: '10px', fontSize: '13px' }}>No groups yet</p>
              </div>
            ) : (
              <>
                <GroupTreeSidebar
                  nodes={buildTree(treeNodes)}
                  selectedId={selectedNodeId}
                  expandedIds={expandedNodeIds}
                  onToggle={handleToggleNode}
                  onSelect={handleSelectNode}
                  renderBadge={(node: GroupTreeNode) => (
                    <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>
                      {node.nodeKind === 'topic' ? '# ' : ''}
                      {node.children.length > 0 ? `${node.children.length}` : ''}
                    </span>
                  )}
                />
                {(selectedNodeId || selectedGroup) && (
                  <div style={{ display: 'flex', gap: '6px', padding: '10px 8px 0' }}>
                    <input
                      type="text"
                      value={newChildName}
                      onChange={(e) => setNewChildName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateChild()}
                      placeholder="+ Sub-group name…"
                      style={{ flex: 1, padding: '8px', background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '12px' }}
                    />
                    <button
                      type="button"
                      onClick={handleCreateChild}
                      disabled={creatingChild || !newChildName.trim()}
                      style={{ padding: '8px 12px', background: '#5865f2', color: 'white', border: 'none', borderRadius: '6px', cursor: creatingChild ? 'wait' : 'pointer', fontSize: '12px' }}
                    >
                      Add
                    </button>
                  </div>
                )}
              </>
            )
          )}

          {activeTab === 'my-groups' && !HIERARCHY_ENABLED && (
            myGroups.length === 0 ? (
              <div style={{ padding: '30px 15px', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                <span style={{ fontSize: '32px' }}>👥</span>
                <p style={{ marginTop: '10px', fontSize: '13px' }}>No groups yet</p>
              </div>
            ) : (
              myGroups.map(group => (
                <div
                  key={group.id}
                  onClick={() => {
                    setSelectedGroup(group);
                    setSelectedChannel(group.channels[0] || null);
                  }}
                  style={{
                    padding: '12px',
                    cursor: 'pointer',
                    background: selectedGroup?.id === group.id ? 'rgba(255,255,255,0.1)' : 'transparent',
                    borderRadius: '8px',
                    marginBottom: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                >
                  <div style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    background: `url(${group.imageUrl}) center/cover, #5865f2`,
                    flexShrink: 0
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h4 style={{
                        margin: 0,
                        fontSize: '13px',
                        color: 'white',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {group.name}
                      </h4>
                      {group.channels.reduce((acc, ch) => acc + ch.unreadCount, 0) > 0 && (
                        <span style={{
                          background: '#ed4245',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>
                          {group.channels.reduce((acc, ch) => acc + ch.unreadCount, 0)}
                        </span>
                      )}
                    </div>
                    <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                      {group.memberCount} members
                    </p>
                  </div>
                </div>
              ))
            )
          )}

          {activeTab === 'discover' && (
            <>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '13px', marginBottom: '8px' }}
              >
                <option value="All" style={{ color: '#333' }}>All categories</option>
                {GROUP_CATEGORIES.slice(1).map(cat => <option key={cat} value={cat} style={{ color: '#333' }}>{cat}</option>)}
              </select>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadDiscoverGroups({ category: selectedCategory, query: searchQuery })}
                placeholder="Search groups..."
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '13px',
                  marginBottom: '8px'
                }}
              />
              <button type="button" onClick={() => loadDiscoverGroups({ category: selectedCategory, query: searchQuery })} disabled={discoverLoading} style={{ width: '100%', padding: '8px', background: '#5865f2', color: 'white', border: 'none', borderRadius: '6px', cursor: discoverLoading ? 'not-allowed' : 'pointer', fontSize: '12px', marginBottom: '10px' }}>
                {discoverLoading ? 'Searching…' : 'Search'}
              </button>
              {discoverLoading && filteredDiscoverGroups.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>Loading…</div>
              ) : filteredDiscoverGroups.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>No groups found. Try a different search.</div>
              ) : (
              filteredDiscoverGroups.map(group => (
                <div key={group.id} style={{ padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: `url(${group.imageUrl}) center/cover, #5865f2` }} />
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: 0, fontSize: '13px', color: 'white' }}>{group.name}</h4>
                      <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>{group.memberCount} members</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleJoinGroup(group)}
                    disabled={joinCheckoutPending === group.id}
                    style={{ width: '100%', marginTop: '10px', padding: '8px', background: '#5865f2', color: 'white', border: 'none', borderRadius: '6px', cursor: joinCheckoutPending === group.id ? 'wait' : 'pointer', fontSize: '12px' }}
                  >
                    {group.membershipFee != null && group.membershipFee > 0
                      ? (joinCheckoutPending === group.id ? 'Redirecting…' : `Pay $${group.membershipFee}/${group.membershipPeriod === 'yearly' ? 'yr' : 'wk'} & Join`)
                      : 'Join'}
                  </button>
                </div>
              ))
              )}
            </>
          )}

          {activeTab === 'create' && (
            <div style={{ padding: '10px' }}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>GROUP NAME</label>
                <input type="text" value={newGroup.name} onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })} placeholder="e.g., Brisbane Runners" style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '13px' }} />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>CATEGORY</label>
                <select value={newGroup.category} onChange={(e) => setNewGroup({ ...newGroup, category: e.target.value })} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '13px' }}>
                  <option value="">Select...</option>
                  {GROUP_CATEGORIES.slice(1).map(cat => <option key={cat} value={cat} style={{ color: '#333' }}>{cat}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>DESCRIPTION</label>
                <textarea value={newGroup.description} onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })} placeholder="What's this group about?" rows={3} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '13px', resize: 'vertical' }} />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>TYPE</label>
                <select value={newGroup.type} onChange={(e) => setNewGroup({ ...newGroup, type: e.target.value as 'personal' | 'community' })} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '13px' }}>
                  <option value="personal" style={{ color: '#333' }}>Personal / Creator (revenue to you)</option>
                  <option value="community" style={{ color: '#333' }}>Community / Non-profit (shared wallet)</option>
                </select>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>MEMBERSHIP FEE (optional, min 50¢/week or $25/year)</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="number" step="0.01" min="0" value={newGroup.membershipFee} onChange={(e) => setNewGroup({ ...newGroup, membershipFee: e.target.value })} placeholder="0.50" style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '13px' }} />
                  <select value={newGroup.membershipPeriod} onChange={(e) => setNewGroup({ ...newGroup, membershipPeriod: e.target.value as 'weekly' | 'yearly' })} style={{ width: '90px', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '13px' }}>
                    <option value="weekly" style={{ color: '#333' }}>/week</option>
                    <option value="yearly" style={{ color: '#333' }}>/year</option>
                  </select>
                </div>
                <p style={{ margin: '5px 0 0 0', fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>Leave empty for free group (one free group per person)</p>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>CUSTOM ROLES (comma separated)</label>
                <input type="text" value={newGroup.customRoles} onChange={(e) => setNewGroup({ ...newGroup, customRoles: e.target.value })} placeholder="e.g., Base, Flyer, Spotter" style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '13px' }} />
                <p style={{ margin: '5px 0 0 0', fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>Create roles specific to your community</p>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                <input type="checkbox" checked={newGroup.isPublic} onChange={(e) => setNewGroup({ ...newGroup, isPublic: e.target.checked })} />
                Public group (anyone can join)
              </label>
              <button onClick={handleCreateGroup} style={{ width: '100%', padding: '12px', background: '#5865f2', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}>
                Create Group
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Channels Sidebar (shown when group selected) */}
      {selectedGroup && (
        <div style={{
          width: '220px',
          background: '#36393f',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Group Header */}
          <div style={{ padding: '15px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 style={{ margin: '0 0 5px 0', fontSize: '15px', fontWeight: '700' }}>{selectedGroup.name}</h3>
            <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
              {selectedGroup.memberCount} members • {selectedGroup.myRole}
            </p>
            {selectedGroup.myCustomRoles && selectedGroup.myCustomRoles.length > 0 && (
              <div style={{ display: 'flex', gap: '5px', marginTop: '8px', flexWrap: 'wrap' }}>
                {selectedGroup.myCustomRoles.map(role => (
                  <span key={role} style={{ padding: '2px 8px', background: '#5865f2', borderRadius: '10px', fontSize: '10px' }}>{role}</span>
                ))}
              </div>
            )}
          </div>

          {/* Channels List */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
            {/* Text Channels */}
            <div style={{ marginBottom: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', marginBottom: '5px' }}>
                <span style={{ fontSize: '11px', fontWeight: '600', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Channels</span>
                {(selectedGroup.myRole === 'admin' || selectedGroup.myRole === 'moderator') && (
                  <button onClick={() => setShowCreateChannel(true)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '16px' }}>+</button>
                )}
              </div>
              {selectedGroup.channels.filter(ch => ch.type !== 'event').map(channel => {
                const hasAccess = canAccessChannel(channel);
                return (
                  <div
                    key={channel.id}
                    onClick={() => hasAccess && setSelectedChannel(channel)}
                    style={{
                      padding: '8px 10px',
                      borderRadius: '4px',
                      cursor: hasAccess ? 'pointer' : 'not-allowed',
                      background: selectedChannel?.id === channel.id ? 'rgba(255,255,255,0.1)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      opacity: hasAccess ? 1 : 0.4,
                      marginBottom: '2px'
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>{channel.icon || '#'}</span>
                    <span style={{ flex: 1, fontSize: '13px', color: selectedChannel?.id === channel.id ? 'white' : 'rgba(255,255,255,0.7)' }}>
                      {channel.name}
                    </span>
                    {channel.isPrivate && <span style={{ fontSize: '10px' }}>🔒</span>}
                    {channel.unreadCount > 0 && (
                      <span style={{ background: '#ed4245', color: 'white', padding: '1px 5px', borderRadius: '8px', fontSize: '10px', fontWeight: '600' }}>
                        {channel.unreadCount}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Events Section */}
            {selectedGroup.upcomingEvents.length > 0 && (
              <div style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', marginBottom: '5px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Events</span>
                  {(selectedGroup.myRole === 'admin' || selectedGroup.myRole === 'moderator') && (
                    <button onClick={() => setShowCreateEvent(true)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '16px' }}>+</button>
                  )}
                </div>
                {selectedGroup.upcomingEvents.map(event => (
                  <div
                    key={event.id}
                    onClick={() => {
                      if (event.hasChat) {
                        const eventChannel = selectedGroup.channels.find(ch => ch.id === event.chatChannelId);
                        if (eventChannel) setSelectedChannel(eventChannel);
                      }
                    }}
                    style={{
                      padding: '10px',
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      cursor: event.hasChat ? 'pointer' : 'default'
                    }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'white', marginBottom: '4px' }}>{event.title}</div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>
                      📅 {new Date(event.date).toLocaleDateString()} at {event.time}
                    </div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
                      👥 {event.attendees.length}{event.maxAttendees ? `/${event.maxAttendees}` : ''} attending
                    </div>
                    {event.hasChat && (
                      <div style={{ fontSize: '10px', color: '#5865f2', marginTop: '5px' }}>💬 Open chat</div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Admin Actions */}
            {(selectedGroup.myRole === 'admin' || selectedGroup.myRole === 'moderator') && (
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '15px', marginTop: '15px' }}>
                <button onClick={() => setShowCreateEvent(true)} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', marginBottom: '8px' }}>
                  📅 Create Event
                </button>
                <button onClick={() => setShowManageRoles(true)} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', marginBottom: '8px' }}>
                  🏷️ Manage Roles
                </button>
                <button onClick={() => setShowGroupSettings(true)} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', marginBottom: '8px' }}>
                  ⚙️ Group Settings
                </button>
              </div>
            )}
            {selectedGroup.membershipFee != null && selectedGroup.membershipFee > 0 && (
              <>
                {membershipInfo?.feeDue && (
                  <div style={{ padding: '8px 10px', background: 'rgba(255,193,7,0.2)', borderRadius: '6px', marginBottom: '8px', fontSize: '12px', color: '#ffc107', fontWeight: 600 }}>
                    Fee due – pay now to stay in good standing
                  </div>
                )}
                {membershipInfo && (
                  <div style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', marginBottom: '8px', fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>
                    {membershipInfo.isLoyaltyEligible
                      ? `★ Member ${membershipInfo.weeksMember} weeks – ${membershipInfo.platformFeePercent}% platform fee (loyalty rate)`
                      : `Member ${membershipInfo.weeksMember} weeks – ${membershipInfo.platformFeePercent}% platform fee (25% after 8+ weeks)`}
                  </div>
                )}
                <button onClick={handlePayMembership} disabled={membershipCheckoutPending} style={{ width: '100%', padding: '10px', background: membershipInfo?.feeDue ? 'rgba(255,193,7,0.3)' : 'rgba(72,187,120,0.3)', color: membershipInfo?.feeDue ? '#ffc107' : '#48bb78', border: `1px solid ${membershipInfo?.feeDue ? 'rgba(255,193,7,0.5)' : 'rgba(72,187,120,0.5)'}`, borderRadius: '6px', cursor: membershipCheckoutPending ? 'wait' : 'pointer', fontSize: '12px', marginBottom: '8px' }}>
                  {membershipCheckoutPending ? 'Redirecting…' : membershipInfo?.feeDue ? `⚠️ Pay fee now ($${selectedGroup.membershipFee}/${selectedGroup.membershipPeriod === 'yearly' ? 'year' : 'week'})` : `💰 Pay membership ($${selectedGroup.membershipFee}/${selectedGroup.membershipPeriod === 'yearly' ? 'year' : 'week'})`}
                </button>
              </>
            )}
            {checkoutMembershipSuccess && (
              <div style={{ padding: '8px 12px', background: 'rgba(72,187,120,0.2)', borderRadius: '6px', marginBottom: '8px', fontSize: '12px', color: '#68d391' }}>
                Payment successful. Your membership fee has been recorded.
              </div>
            )}
            <button onClick={handleLeaveGroup} style={{ width: '100%', padding: '10px', background: 'rgba(237,66,69,0.3)', color: '#ed4245', border: '1px solid rgba(237,66,69,0.5)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
              Leave group
            </button>
          </div>
        </div>
      )}

      {showGroupSettings && selectedGroup && (
        <GroupSettingsModal
          group={selectedGroup}
          onClose={() => { setShowGroupSettings(false); setGroupSettings(null); setGroupSettingsDraft({}); setCheckoutSuccessCredits(null); }}
          onOpen={async () => {
            const res = await groupsApi.getGroupSettings(selectedGroup.id);
            if (res?.success && res?.data) setGroupSettings(res.data);
            else setGroupSettings('error');
          }}
          settings={groupSettings}
          draft={groupSettingsDraft}
          onDraftChange={setGroupSettingsDraft}
          checkoutSuccessCredits={checkoutSuccessCredits}
          onSave={async () => {
            const updates: {
              cronSecret?: string | null;
              aiOrganizingEnabled?: boolean;
              addAiCredits?: number;
              aiChatEnabled?: boolean;
              aiChatBillingMode?: AiFeatureBillingMode;
              translationBillingMode?: AiFeatureBillingMode;
              transcriptionBillingMode?: AiFeatureBillingMode;
              autoBookingEnabled?: boolean;
              autoBookingBillingMode?: AiFeatureBillingMode;
            } = {};
            if (groupSettingsDraft.cronSecret !== undefined) updates.cronSecret = groupSettingsDraft.cronSecret || null;
            if (groupSettingsDraft.aiOrganizingEnabled !== undefined) updates.aiOrganizingEnabled = groupSettingsDraft.aiOrganizingEnabled;
            if (groupSettingsDraft.addCredits) {
              const n = parseInt(groupSettingsDraft.addCredits, 10);
              if (!isNaN(n) && n > 0) updates.addAiCredits = n;
            }
            if (groupSettingsDraft.aiChatEnabled !== undefined) updates.aiChatEnabled = groupSettingsDraft.aiChatEnabled;
            if (groupSettingsDraft.aiChatBillingMode !== undefined) updates.aiChatBillingMode = groupSettingsDraft.aiChatBillingMode;
            if (groupSettingsDraft.translationBillingMode !== undefined) updates.translationBillingMode = groupSettingsDraft.translationBillingMode;
            if (groupSettingsDraft.transcriptionBillingMode !== undefined) updates.transcriptionBillingMode = groupSettingsDraft.transcriptionBillingMode;
            if (groupSettingsDraft.autoBookingEnabled !== undefined) updates.autoBookingEnabled = groupSettingsDraft.autoBookingEnabled;
            if (groupSettingsDraft.autoBookingBillingMode !== undefined) updates.autoBookingBillingMode = groupSettingsDraft.autoBookingBillingMode;
            if (Object.keys(updates).length === 0) return { success: true };
            const res = await groupsApi.updateGroupSettings(selectedGroup.id, updates);
            if (res?.success) {
              setGroupSettings(prev => prev && prev !== 'error' ? {
                ...prev,
                aiCreditsBalance: res.aiCreditsBalance ?? prev.aiCreditsBalance,
                aiOrganizingEnabled: res.aiOrganizingEnabled ?? prev.aiOrganizingEnabled,
                cronSecretSet: res.cronSecretSet ?? prev.cronSecretSet,
                aiChatEnabled: updates.aiChatEnabled ?? prev.aiChatEnabled,
                aiChatBillingMode: updates.aiChatBillingMode ?? prev.aiChatBillingMode,
                translationBillingMode: updates.translationBillingMode ?? prev.translationBillingMode,
                transcriptionBillingMode: updates.transcriptionBillingMode ?? prev.transcriptionBillingMode,
                autoBookingEnabled: updates.autoBookingEnabled ?? prev.autoBookingEnabled,
                autoBookingBillingMode: updates.autoBookingBillingMode ?? prev.autoBookingBillingMode,
              } : prev);
              setGroupSettingsDraft({});
            }
            return res;
          }}
        />
      )}

      {/* Main Chat Area */}
      <div style={{
        flex: 1,
        background: '#36393f',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: selectedGroup ? '0 16px 16px 0' : '0 16px 16px 0'
      }}>
        {selectedChannel ? (
          <>
            {/* Channel Header */}
            <div style={{ padding: '15px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ fontSize: '20px' }}>{selectedChannel.icon}</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', color: 'white', fontWeight: '600' }}>{selectedChannel.name}</h3>
                  {selectedChannel.description && (
                    <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{selectedChannel.description}</p>
                  )}
                </div>
                {selectedChannel.isPrivate && (
                  <span style={{ marginLeft: 'auto', padding: '4px 10px', background: 'rgba(255,255,255,0.1)', borderRadius: '10px', fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>
                    🔒 {selectedChannel.allowedRoles?.join(', ') || 'Private'}
                  </span>
                )}
              </div>
              {/* Community Groups: List product button + channel view filters */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowCreateProduct(true)}
                  style={{
                    padding: '4px 10px',
                    background: 'rgba(114,137,218,0.5)',
                    border: '1px solid rgba(114,137,218,0.8)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  🛒 List product
                </button>
                <select
                  value={selectedGroup?.myPhotoPostingMode || 'manual'}
                  onChange={(e) => handleChangePhotoPostingMode(e.target.value as 'manual' | 'prompt' | 'auto')}
                  title="How should photos from this group's events get shared? 'Auto' behaves like 'Prompt after event' until the native app exists."
                  style={{
                    padding: '4px 10px',
                    background: 'rgba(255,255,255,0.06)',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  <option value="manual" style={{ color: '#333' }}>📸 Photos: Manual</option>
                  <option value="prompt" style={{ color: '#333' }}>📸 Photos: Prompt after event</option>
                  <option value="auto" style={{ color: '#333' }}>📸 Photos: Auto (needs native app — uses Prompt for now)</option>
                </select>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {CHANNEL_VIEWS.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setChannelViewFilter(v.id)}
                    style={{
                      padding: '4px 10px',
                      background: channelViewFilter === v.id ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.06)',
                      border: 'none',
                      borderRadius: '6px',
                      color: 'white',
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                  >
                    {v.icon} {v.label}
                  </button>
                ))}
                </div>
              </div>
            </div>

            {/* Polls in channel area */}
            {channelPolls.length > 0 && (
              <div style={{ padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '10px' }}>📊 Active polls</div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {channelPolls.filter(p => !(p.isExpired || (p.expiresAt && new Date(p.expiresAt) < new Date()))).slice(0, 3).map(p => {
                    const opts = p.options || [];
                    const counts = p.optionCounts || opts.map(() => 0);
                    const total = p.totalVotes ?? counts.reduce((a, b) => a + b, 0);
                    return (
                      <div key={p.id} style={{ flex: '1 1 200px', maxWidth: '280px', padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'white', marginBottom: '8px' }}>{p.question}</div>
                        {opts.map((opt, i) => {
                          const cnt = counts[i] ?? 0;
                          const pct = total > 0 ? Math.round((cnt / total) * 100) : 0;
                          const isMyVote = p.myVote === i;
                          return (
                            <button key={i} type="button" onClick={() => handleChannelVote(p.id, i)}
                              style={{ display: 'block', width: '100%', padding: '6px 10px', marginBottom: '4px', textAlign: 'left', border: `1px solid ${isMyVote ? 'rgba(114,137,218,0.8)' : 'rgba(255,255,255,0.2)'}`, borderRadius: '4px', cursor: 'pointer', fontSize: '12px', background: isMyVote ? 'rgba(114,137,218,0.2)' : 'transparent', color: 'white' }}>
                              {opt} {total > 0 && ` (${cnt}, ${pct}%)`}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
                {channelPolls.filter(p => !(p.isExpired || (p.expiresAt && new Date(p.expiresAt) < new Date()))).length > 3 && (
                  <button type="button" onClick={() => setShowGroupSettings(true)} style={{ marginTop: '8px', fontSize: '11px', color: '#7289da', background: 'none', border: 'none', cursor: 'pointer' }}>View all polls in settings →</button>
                )}
              </div>
            )}

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
              {messages.map(msg => (
                <div key={msg.id} style={{ marginBottom: '20px', display: 'flex', gap: '15px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: msg.senderAvatar ? `url(${msg.senderAvatar}) center/cover` : 'linear-gradient(135deg, #5865f2, #7289da)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    flexShrink: 0
                  }}>
                    {!msg.senderAvatar && msg.senderName.charAt(0)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                      <span style={{ fontWeight: '600', fontSize: '14px', color: 'white' }}>{msg.senderName}</span>
                      <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p style={{ margin: '6px 0 0 0', fontSize: '14px', color: 'rgba(255,255,255,0.9)', lineHeight: 1.5 }}>{msg.content}</p>
                    {/* Product collage card: photos + sticker overlays + link */}
                    {msg.linkedProductId && msg.linkedProduct && selectedGroup && (
                      <div style={{ marginTop: '10px' }}>
                        <ProductCollageCard product={msg.linkedProduct} groupId={selectedGroup.id} />
                      </div>
                    )}
                    {/* Attachments */}
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                        {msg.attachments.map((att, i) => (
                          att.type === 'image' ? (
                            <img key={i} src={att.url} alt={att.name} style={{ maxWidth: '250px', maxHeight: '200px', borderRadius: '8px', cursor: 'pointer' }} />
                          ) : (
                            <div key={i} style={{ padding: '10px 15px', background: 'rgba(255,255,255,0.1)', borderRadius: '6px', fontSize: '13px', color: 'white' }}>
                              📎 {att.name}
                            </div>
                          )
                        ))}
                      </div>
                    )}
                    {/* Reactions */}
                    {msg.reactions && msg.reactions.length > 0 && (
                      <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                        {msg.reactions.map((r, i) => (
                          <span key={i} style={{ padding: '2px 8px', background: 'rgba(255,255,255,0.1)', borderRadius: '10px', fontSize: '12px', cursor: 'pointer' }}>
                            {r.emoji} {r.count}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Message Input */}
            <div style={{ padding: '15px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              {/* Optional tag (Community Groups: Product, Event, Recommendation, General) */}
              <div style={{ marginBottom: '8px' }}>
                <select
                  value={messageTag}
                  onChange={(e) => setMessageTag(e.target.value as any)}
                  style={{
                    padding: '4px 10px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '6px',
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                  title="Tag this message (optional)"
                >
                  <option value="" style={{ color: '#333' }}>No tag (general)</option>
                  <option value="product" style={{ color: '#333' }}>🛒 Product</option>
                  <option value="event" style={{ color: '#333' }}>📅 Event</option>
                  <option value="recommendation" style={{ color: '#333' }}>👍 Recommendation</option>
                  <option value="general" style={{ color: '#333' }}>💬 General</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploadingPhotos}
                  title="Take a photo"
                  style={{ width: '40px', height: '40px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', cursor: uploadingPhotos ? 'not-allowed' : 'pointer', fontSize: '18px', opacity: uploadingPhotos ? 0.5 : 1 }}
                >
                  📷
                </button>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => { handleAttachPhotos(e.target.files); e.target.value = ''; }}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhotos}
                  title="Attach photos"
                  style={{ width: '40px', height: '40px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', cursor: uploadingPhotos ? 'not-allowed' : 'pointer', fontSize: '18px', opacity: uploadingPhotos ? 0.5 : 1 }}
                >
                  {uploadingPhotos ? '…' : '📎'}
                </button>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => { handleAttachPhotos(e.target.files); e.target.value = ''; }}
                  style={{ display: 'none' }}
                />
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder={`Message #${selectedChannel.name}`}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    background: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    borderRadius: '20px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  style={{
                    padding: '12px 20px',
                    background: newMessage.trim() ? '#5865f2' : 'rgba(255,255,255,0.1)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  Send
                </button>
              </div>
            </div>
          </>
        ) : selectedNodeId && !selectedGroup ? (
          // Node visible only via roll-up/roll-down aggregation (not a direct membership) —
          // lightweight read-mostly panel: breadcrumb + aggregated feed with origin chips.
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              {nodeAncestors.map((a) => (
                <React.Fragment key={a.id}>
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }} onClick={() => handleSelectNode(a.id)}>
                    {a.name}
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.3)' }}>/</span>
                </React.Fragment>
              ))}
              <span style={{ fontSize: '13px', color: 'white', fontWeight: 600, flex: 1 }}>
                {treeNodes.find((n) => n.id === selectedNodeId)?.name || ''}
              </span>
              <button
                type="button"
                onClick={() => setNodeFeedScope((s) => (s === 'subtree' ? 'node' : 'subtree'))}
                style={{ padding: '6px 12px', background: nodeFeedScope === 'subtree' ? '#5865f2' : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '11px', cursor: 'pointer' }}
              >
                {nodeFeedScope === 'subtree' ? '✓ Include sub-groups' : 'Include sub-groups'}
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
              {nodeFeedLoading ? (
                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '30px' }}>Loading…</div>
              ) : nodeFeed.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '30px' }}>No messages yet</div>
              ) : (
                nodeFeed.map((m: any) => (
                  <div key={m.id} style={{ marginBottom: '14px' }}>
                    {nodeFeedScope === 'subtree' && m.nodeId !== selectedNodeId && (
                      <span style={{ fontSize: '10px', color: '#5865f2', marginRight: '6px' }}>#{m.nodeName}</span>
                    )}
                    <span style={{ fontSize: '13px', color: 'white' }}>{m.content}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: 'rgba(255,255,255,0.5)' }}>
            <span style={{ fontSize: '64px', marginBottom: '20px' }}>👥</span>
            <h3 style={{ margin: '0 0 8px 0', color: 'white' }}>
              {selectedGroup ? 'Select a channel' : 'Select a group'}
            </h3>
            <p style={{ margin: 0, fontSize: '14px' }}>
              {selectedGroup ? 'Choose a channel from the sidebar to start chatting' : 'Choose a group from the sidebar to get started'}
            </p>
          </div>
        )}
      </div>

      {/* Join paid group confirm modal */}
      {joinConfirmGroup && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setJoinConfirmGroup(null)}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '380px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', color: '#1a1a1a' }}>Join {joinConfirmGroup.name}</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#555' }}>
              This group charges ${joinConfirmGroup.membershipFee} per {joinConfirmGroup.membershipPeriod === 'yearly' ? 'year' : 'week'}. You'll be redirected to Stripe to complete payment, then added as a member.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setJoinConfirmGroup(null)} style={{ padding: '10px 18px', background: '#e0e0e0', color: '#333', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
              <button type="button" onClick={handleConfirmJoinCheckout} style={{ padding: '10px 18px', background: '#5865f2', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>Continue to payment</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Product Modal */}
      {showCreateProduct && selectedGroup && selectedChannel && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowCreateProduct(false)}>
          <div style={{ background: '#36393f', borderRadius: '12px', padding: '25px', width: '400px', color: 'white' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>🛒 List product in group</h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>TITLE *</label>
              <input type="text" value={newProduct.title} onChange={(e) => setNewProduct({ ...newProduct, title: e.target.value })} placeholder="e.g. North Face backpack size L" style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>DESCRIPTION / HIGHLIGHTS</label>
              <textarea value={newProduct.description} onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })} placeholder="One highlight per line (e.g. RWC, Both wheels replaced, Perfect for local trips)" rows={4} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '14px', resize: 'vertical' }} />
              <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Each line becomes a ✅ bullet on the product card</p>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>PRICE (optional)</label>
              <input type="text" value={newProduct.price} onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })} placeholder="e.g. 50" style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>IMAGE URLS (1–5, comma-separated)</label>
              <input type="text" value={newProduct.imageUrls} onChange={(e) => setNewProduct({ ...newProduct, imageUrls: e.target.value })} placeholder="https://..." style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '14px' }} />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowCreateProduct(false)} style={{ padding: '10px 18px', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
              <button type="button" onClick={() => handleCreateProduct(false)} style={{ padding: '10px 18px', background: '#5865f2', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Create only</button>
              <button type="button" onClick={() => handleCreateProduct(true)} style={{ padding: '10px 18px', background: '#57f287', color: '#2f3136', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>Create & Share in chat</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Channel Modal */}
      {showCreateChannel && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#36393f', borderRadius: '12px', padding: '25px', width: '400px', color: 'white' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>Create Channel</h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>CHANNEL NAME</label>
              <input type="text" value={newChannel.name} onChange={(e) => setNewChannel({ ...newChannel, name: e.target.value })} placeholder="e.g., training-tips" style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>TYPE</label>
              <select value={newChannel.type} onChange={(e) => setNewChannel({ ...newChannel, type: e.target.value as any })} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '14px' }}>
                <option value="general" style={{ color: '#333' }}>💬 General</option>
                <option value="announcement" style={{ color: '#333' }}>📢 Announcements</option>
                <option value="training" style={{ color: '#333' }}>💪 Training</option>
                <option value="social" style={{ color: '#333' }}>🎉 Social</option>
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px', cursor: 'pointer', fontSize: '13px' }}>
              <input type="checkbox" checked={newChannel.isPrivate} onChange={(e) => setNewChannel({ ...newChannel, isPrivate: e.target.checked })} />
              🔒 Private channel (restrict access by role)
            </label>
            {newChannel.isPrivate && selectedGroup?.customRoles && selectedGroup.customRoles.length > 0 && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>WHO CAN ACCESS</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedGroup.customRoles.map(role => (
                    <label key={role} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', fontSize: '12px' }}>
                      <input
                        type="checkbox"
                        checked={newChannel.allowedRoles.includes(role)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewChannel({ ...newChannel, allowedRoles: [...newChannel.allowedRoles, role] });
                          } else {
                            setNewChannel({ ...newChannel, allowedRoles: newChannel.allowedRoles.filter(r => r !== role) });
                          }
                        }}
                      />
                      {role}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={() => setShowCreateChannel(false)} style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleCreateChannel} style={{ flex: 1, padding: '10px', background: '#5865f2', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Create Channel</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      {showCreateEvent && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#36393f', borderRadius: '12px', padding: '25px', width: '450px', color: 'white', maxHeight: '80vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>📅 Create Event</h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>EVENT TITLE</label>
              <input type="text" value={newEvent.title} onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })} placeholder="e.g., Beach Jam Session" style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>DESCRIPTION</label>
              <textarea value={newEvent.description} onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })} placeholder="What's the event about?" rows={3} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '14px', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>DATE</label>
                <input type="date" value={newEvent.date} onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '14px' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>TIME</label>
                <input type="time" value={newEvent.time} onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '14px' }} />
              </div>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>LOCATION</label>
              <input type="text" value={newEvent.location} onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })} placeholder="e.g., South Bank Beach" style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>MAX ATTENDEES (optional)</label>
              <input type="number" value={newEvent.maxAttendees} onChange={(e) => setNewEvent({ ...newEvent, maxAttendees: e.target.value })} placeholder="Leave blank for unlimited" style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '14px' }} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px', cursor: 'pointer', fontSize: '13px' }}>
              <input type="checkbox" checked={newEvent.createChat} onChange={(e) => setNewEvent({ ...newEvent, createChat: e.target.checked })} />
              💬 Create a dedicated chat channel for this event
            </label>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>📸 ASK ATTENDEES FOR PHOTOS AFTER</label>
              <select
                value={newEvent.photoPromptDelayMinutes}
                onChange={(e) => setNewEvent({ ...newEvent, photoPromptDelayMinutes: parseInt(e.target.value) })}
                style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '14px' }}
              >
                <option value={30} style={{ color: '#333' }}>30 minutes</option>
                <option value={60} style={{ color: '#333' }}>1 hour</option>
                <option value={120} style={{ color: '#333' }}>2 hours</option>
                <option value={1440} style={{ color: '#333' }}>End of day</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowCreateEvent(false)} style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleCreateEvent} style={{ flex: 1, padding: '10px', background: '#5865f2', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Create Event</button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Roles Modal */}
      {showManageRoles && selectedGroup && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowManageRoles(false)}>
          <div style={{ background: '#36393f', borderRadius: '12px', padding: '25px', width: '420px', maxHeight: '80vh', overflowY: 'auto', color: 'white' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>🏷️ Manage Roles</h3>
            {selectedGroup.type === 'community' && selectedGroup.myRole === 'admin' && (
              <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(102,126,234,0.2)', borderRadius: '8px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: 600 }}>Invite admin by email</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="email" placeholder="email@example.com" value={inviteAdminEmail} onChange={e => setInviteAdminEmail(e.target.value)}
                    style={{ flex: 1, padding: '8px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white' }} />
                  <button onClick={async () => {
                    if (!inviteAdminEmail.trim()) return;
                    setInviteAdminSending(true);
                    const res = await groupsApi.inviteAdmin(selectedGroup.id, inviteAdminEmail.trim());
                    if (res?.success) { setInviteAdminEmail(''); showToast('Invite sent!', 'success'); }
                    else showToast(res?.error || 'Failed to send', 'error');
                    setInviteAdminSending(false);
                  }} disabled={inviteAdminSending || !inviteAdminEmail.trim()}
                    style={{ padding: '8px 16px', background: '#5865f2', color: 'white', border: 'none', borderRadius: '6px', cursor: inviteAdminSending ? 'wait' : 'pointer' }}>{inviteAdminSending ? 'Sending…' : 'Invite'}</button>
                </div>
              </div>
            )}
            {(selectedGroup.myRole === 'admin' || selectedGroup.myRole === 'moderator') && (
              <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(67,181,129,0.15)', borderRadius: '8px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: 600 }}>Invite link (joins as member)</label>
                {generatedInviteLink ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input readOnly value={generatedInviteLink}
                      style={{ flex: 1, padding: '8px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'white', fontSize: '12px' }} />
                    <button onClick={() => { navigator.clipboard?.writeText(generatedInviteLink); showToast('Copied!', 'success'); }}
                      style={{ padding: '8px 16px', background: '#43b581', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Copy</button>
                  </div>
                ) : (
                  <button onClick={async () => {
                    setInviteLinkGenerating(true);
                    const res = await groupsApi.createInviteLink(selectedGroup.id);
                    if (res?.success) setGeneratedInviteLink(res.data?.joinUrl || '');
                    else showToast(res?.error || 'Failed to create invite link', 'error');
                    setInviteLinkGenerating(false);
                  }} disabled={inviteLinkGenerating}
                    style={{ padding: '8px 16px', background: '#43b581', color: 'white', border: 'none', borderRadius: '6px', cursor: inviteLinkGenerating ? 'wait' : 'pointer' }}>{inviteLinkGenerating ? 'Generating…' : 'Generate link to share'}</button>
                )}
              </div>
            )}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>MEMBERS – Promote or demote</label>
              {manageRolesMembers.length === 0 ? (
                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>Loading…</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {manageRolesMembers.map(m => (
                    <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px' }}>
                      <span style={{ flex: 1, fontSize: '13px' }}>{m.name || m.email || m.userId}</span>
                      <select value={m.role} onChange={async e => {
                        const role = e.target.value as 'admin' | 'moderator' | 'member';
                        const res = await groupsApi.updateMemberRole(selectedGroup.id, m.userId, role);
                        if (res?.success) setManageRolesMembers(prev => prev.map(x => x.userId === m.userId ? { ...x, role } : x));
                        else showToast(res?.error || 'Failed to update role', 'error');
                      }} style={{ padding: '4px 8px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', color: 'white', fontSize: '12px' }}>
                        <option value="admin">Admin</option>
                        <option value="moderator">Moderator</option>
                        <option value="member">Member</option>
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedGroup.type === 'community' && selectedGroup.minimumAdmins && (
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '15px' }}>Community groups require at least {selectedGroup.minimumAdmins} admins.</p>
            )}
            <button onClick={() => setShowManageRoles(false)} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Groups;
